import React from 'react';
import {
	AttributeVocabularyLookup,
	type AttributeVocabularyLookupProps,
} from '@netx/core-attribute-vocabulary/components/AttributeVocabularyLookup';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {splitEventHandler} from '@netx/core-next/utils';
import {i18n} from '@netx/core/i18n';
import {usePlaceholder} from '../../../../hooks';
import type {CommonProps} from '../../types';

export interface SelectAttributeProps
	extends Omit<CommonProps<string>, 'onChange' | 'value'>,
		Omit<AttributeVocabularyLookupProps<false>, 'vocabularyId'> {}

export const SelectAttributePrimitive = ({
	action,
	attrData,
	hideLabel,
	isMetadataLookup,
	isMetadataTarget,
	multipleValues,
	placeholder = '',
	readOnly,
	value,
	onChange,
	onLookup,
	// so it does no get passed along
	// only some of our attribute types are offer folksonomy
	disableFolksonomy,
	// only some of our attribute types are searchable
	onSearch,
	...props
}: SelectAttributeProps) => {
	const cleared = action === 'clear';

	const textfieldPlaceholder = usePlaceholder(
		attrData,
		placeholder !== '' ? placeholder : i18n.t('i18n.chooseOne'),
		{
			cleared,
			multipleValues,
			readOnly,
			value,
		},
	);

	const handleChange = splitEventHandler(
		onChange,
		useConstCallback((event, value) => {
			if (value == null || !isMetadataLookup) return;
			onLookup?.(value);
		}),
	);

	return (
		<AttributeVocabularyLookup<false>
			hideErrorMessage
			multiple={false}
			id={attrData.attributeid.toString()}
			label={hideLabel ? undefined : attrData.name}
			name={attrData.name}
			{...props}
			placeholder={textfieldPlaceholder}
			readOnly={readOnly}
			value={value}
			vocabularyId={attrData.spec1}
			onChange={handleChange}
			InputProps={{
				InputLabelProps: {
					shrink: multipleValues || cleared ? true : undefined,
				},
			}}
		/>
	);
};

export const SelectAttribute = React.memo(SelectAttributePrimitive);

export default SelectAttribute;
